import React from "react";
import {
  ElementInfo,
  ExposedInlet,
  ExposedOutlet,
  ExposedActions,
  ExposedOutletArgument,
} from "@citychallenge/common";
import { Subject, Subscription } from "rxjs";

export class NoContextError extends Error {}
export class NotDecoratedError extends Error {}

export type ConnectSubjectItem = {
  subject: Subject<any>;
  arguments?: { [key: string]: ExposedOutletArgument };
  repeatable?: boolean;
};
export interface ConnectSubjectMap {
  [key: string]: ConnectSubjectItem;
}

export interface ConnectActions {
  [key: string]: Function;
}

export const ReactiveContext = React.createContext({
  connect: (
    _id: string,
    _inlets: ConnectSubjectMap,
    _outlet: ConnectSubjectMap,
    _actions?: ConnectSubjectMap
  ) => {
    // Connect cannot be an empty implementation because typescript will check if connect parameter is provider in Provider
    // throw new NoContextError("Using the reactive connect context without provider.")
  },
});

export interface ConnectProps {
  elementId: string;
}

export interface ConnectReceivedProps {
  elementId?: string;
  connectActions?: (actions: ConnectActions) => void;
}

export const ReactiveChildContext = React.createContext({
  registerInlets: (_inlets: ExposedInlet) => {
    // Context placeholder
  },
  registerOutlets: (_outlets: ExposedOutlet) => {
    // Context placeholder
  },
});

export function connect<P extends {}>(
  elementInfo: ElementInfo<P>,
  Component: React.ComponentType<P>,
  additionalInlets: ExposedInlet[] = []
): React.ComponentType<P & ConnectProps> & { elementInfo: ElementInfo<P> } {
  class Connect extends React.Component<P & ConnectProps> {
    public static contextType = ReactiveContext;
    private actions?: ConnectActions;

    private subsciptions: Subscription[] = [];
    private mergedProps: { [key: string]: any } = {};

    constructor(
      props: P & ConnectProps,
      context: React.Context<typeof ReactiveContext>
    ) {
      super(props, context);
      this.initiate();
    }

    public static get elementInfo() {
      return elementInfo;
    }

    private initiate() {
      const { elementInfo } = Connect;

      if (!this.context || !this.context.connect) {
        throw new NoContextError(
          "Reactive Connect HOC does not have a valid context! Got context: " +
            this.context
        );
      }

      // Expand given props onto merged props
      this.mergedProps = { ...this.props };
      this.mergedProps.connectActions = (actions: ConnectActions) => {
        this.actions = actions;
      };

      // Connect elements using context
      this.context.connect(
        this.props.elementId || "element",
        this.registerInlets([...elementInfo.inlets, ...additionalInlets]),
        this.registerOutlets(elementInfo.outlets),
        this.registerActions(elementInfo.options.actions)
      );
    }

    private registerInlets(values: ExposedInlet[]) {
      const mappedInlets: ConnectSubjectMap = {};

      for (const inlet of values) {
        const subject = new Subject();
        mappedInlets[inlet.property] = { subject };
        this.subsciptions.push(
          subject.subscribe((value) =>
            this._inletUpdated(inlet.property, value)
          )
        );
        this.mergedProps[inlet.property] = (
          this.props as {
            [key: string]: any;
          }
        )[inlet.property];
      }

      return mappedInlets;
    }

    private registerOutlets(values: ExposedOutlet[]) {
      const mappedOutlets: ConnectSubjectMap = {};

      for (const outlet of values) {
        const subject = new Subject();

        mappedOutlets[outlet.property] = {
          subject: subject,
          arguments: outlet.arguments,
          repeatable: outlet.repeatable,
        };

        this.mergedProps[outlet.property] = subject.next.bind(subject);
      }

      return mappedOutlets;
    }

    // Register actions if they exist
    private registerActions(actions?: ExposedActions) {
      if (!actions) return undefined;
      const mappedActions: ConnectSubjectMap = {};
      for (const [key] of Object.entries(actions)) {
        const subject = new Subject();
        mappedActions[key] = { subject };

        this.subsciptions.push(
          subject.subscribe((value) => {
            const action = this.actions && this.actions[key];
            if (action) {
              action(value);
            }
          })
        );
      }

      return mappedActions;
    }

    private _inletUpdated(key: string, value: any) {
      this.mergedProps[key] = value;
      this.forceUpdate();
    }

    public render() {
      return <Component {...(this.mergedProps as P)} />;
    }
  }

  return Connect;
}
export default connect;
